import { DeepLinkRegistry } from "./deepLinkRegistry";

/**
 * Initializes the deep linking logic for the application.
 * This function checks the current URL for whitelisted parameters (e.g., ?video=123),
 * attempts to find a registered element matching the parameter, and triggers the appropriate action/event.
 * - Only the last whitelisted parameter is considered if multiple are present.
 * - If the element is inside a carousel, a scroll event is dispatched before the action.
 * - If the element is not found within MAX_SEARCH_TIME_MS, the search is aborted.
 */
function initializeDeepLinker() {
  console.log("[Conductor] Initializing Deep Linker...");
  let EVENT_NAME = "deeplink:trigger-action"; // Default event name, will be replaced if a match is found.
  const MAX_SEARCH_TIME_MS = 5000; // Maximum time to search for the element before giving up.
  const SCROLL_DELAY_MS = 700; // Delay after scrolling before dispatching the action event.
  const PARAMS_WHITELIST = ["video"]; // Only these URL params are considered for deep linking.

  // Parse URL parameters.
  let urlParams = new URLSearchParams(window.location.search);
  const paramsKeysArr = [...urlParams.keys()];

  // If multiple whitelisted params exist, keep only the last one.
  if (paramsKeysArr.length > 1) {
    const urlParamsLastParamKey = paramsKeysArr
      .filter((key) => PARAMS_WHITELIST.includes(key))
      .pop();
    // Delete all other params except the last whitelisted one.
    paramsKeysArr.forEach((key) => {
      if (key !== urlParamsLastParamKey) {
        urlParams.delete(key);
      }
    });
  }

  // If no whitelisted params are present, exit early and clear the registry.
  if (
    urlParams.size === 0 ||
    ![...urlParams.keys()].some((key) => PARAMS_WHITELIST.includes(key))
  ) {
    console.log(
      "[Conductor] No URL parameters found. Exiting deep link logic.",
    );
    DeepLinkRegistry.active = false;
    DeepLinkRegistry.clear();
    return;
  }

  // State for the search loop.
  let startTime = null;
  let foundElement = null;
  let foundParamKey = null;
  let foundValue = null;
  let isCarouselItem = false;

  /**
   * Attempts to find the target element and trigger the appropriate event.
   * Uses requestAnimationFrame to repeatedly search for the element until found or timeout.
   * @param {DOMHighResTimeStamp} timestamp - Provided by requestAnimationFrame.
   */
  function findAndTrigger(timestamp) {
    if (!startTime) {
      startTime = timestamp;
    }

    // Calculate elapsed time since search started.
    const elapsedTime = timestamp - startTime;

    // Abort if search takes too long.
    if (elapsedTime > MAX_SEARCH_TIME_MS) {
      console.warn(
        `[Conductor] Deep link target not found within ${MAX_SEARCH_TIME_MS}ms.`,
      );
      return;
    }

    // Iterate through all registered searchers and URL params to find a match.
    // This loop only runs until an element is found for the first time.
    if (!foundElement) {
      for (let [paramKey, rawValue] of urlParams.entries()) {
        // If multiple values are present (comma-separated), use the last one.
        let finalValue = rawValue;
        if (rawValue.includes(",")) {
          const valueArray = rawValue.split(",").map((item) => item.trim());
          finalValue = valueArray[valueArray.length - 1];
        }

        // Attempt to find a registered element for this param/finalValue.
        const { element, isCarouselSlide } =
          DeepLinkRegistry.find(`deeplink-${finalValue}`, finalValue) || {};
        if (element) {
          foundElement = element;
          foundParamKey = paramKey;
          foundValue = finalValue;
          isCarouselItem = isCarouselSlide;
          break; // Found our target, stop searching.
        }
      }
    }

    if (foundElement) {
      // Element found! Prepare to trigger the action.
      DeepLinkRegistry.active = false;
      console.log(
        `[Conductor] Found element via "${foundParamKey}" param. Initiating action.`,
      );

      EVENT_NAME = `deeplink:${foundValue}`;
      DeepLinkRegistry.setFoundElement({
        element: foundElement,
        isCarouselSlide: isCarouselItem,
      });

      // Prepare the custom event to signal the element.
      const actionEvent = new CustomEvent(EVENT_NAME, {
        bubbles: true,
        detail: {
          triggeredBy: "url_param",
          paramKey: foundParamKey,
          value: foundValue,
        },
      });

      if (isCarouselItem) {
        // If the element is inside a carousel, scroll the carousel into view first.
        console.log(
          `[Conductor] Element is inside a carousel. Dispatching carousel scroll event first.`,
        );
        const swiperWrapper = foundElement.closest(".swiper-wrapper");

        if (swiperWrapper) {
          // Dispatch a custom event to signal carousel scroll.
          const carouselEvent = new CustomEvent("deeplink:carousel-scroll", {
            bubbles: true,
          });

          swiperWrapper.scrollIntoView({
            inline: "center",
            block: "center",
            behavior: "smooth",
          });

          swiperWrapper?.dispatchEvent(carouselEvent);
        }
      } else {
        // Otherwise, scroll the element itself into view.
        foundElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "center",
        });
      }

      // After scrolling, dispatch the action event to the element.
      setTimeout(() => {
        console.log(
          `[Conductor] Dispatching signal "${EVENT_NAME}" to the element.`,
        );
        foundElement.dispatchEvent(actionEvent);
      }, SCROLL_DELAY_MS);
    } else {
      // Element not found yet, try again on the next frame.
      requestAnimationFrame(findAndTrigger);
    }
  }

  // Start the search loop.
  requestAnimationFrame(findAndTrigger);
}

// Export the initializer for use in the application entry point.
export default initializeDeepLinker;
